import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import {
  FaCheck as FaCheckRaw,
  FaChevronRight as FaChevronRightRaw,
  FaClock as FaClockRaw,
  FaEnvelope as FaEnvelopeRaw,
  FaMagic as FaMagicRaw,
  FaSearch as FaSearchRaw,
} from 'react-icons/fa';
import { teacherService, type TeacherGradeRow } from '../../services/classroom/teacherService';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCheck: any = FaCheckRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaChevronRight: any = FaChevronRightRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaClock: any = FaClockRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEnvelope: any = FaEnvelopeRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaMagic: any = FaMagicRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaSearch: any = FaSearchRaw;

const MOBILE_BREAKPOINT = '960px';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 0 0 1.5rem;
`;

const Hero = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1.5rem 1.75rem;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const HeroText = styled.div`
  min-width: 0;
  flex: 1;
`;

const HeroTitle = styled.h2`
  margin: 0 0 0.45rem;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.35rem, 2.5vw, 1.75rem);
  font-weight: 700;
  color: var(--text);
  letter-spacing: -0.02em;
`;

const HeroSubtitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  line-height: 1.5;
  max-width: 36rem;
`;

const BulkButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.15rem;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
  color: var(--onPrimary);
  background: var(--primary);
  border-radius: 10px;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: background 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    background: var(--primaryDark);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.article<{ $accent: 'primary' | 'warning' | 'deep' }>`
  background: var(--surface);
  border-radius: 12px;
  padding: 0.85rem 1rem 0.85rem 0.9rem;
  border: 1px solid var(--outline);
  border-left-width: 4px;
  border-left-color: ${({ $accent }) =>
    $accent === 'primary' ? 'var(--primary)' : $accent === 'warning' ? 'var(--warning, #e8a23c)' : 'var(--primaryDark)'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const StatLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--textSecondary, var(--onSurfaceVariant));
  margin-bottom: 0.45rem;
`;

const StatValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  flex-wrap: wrap;
`;

const StatValue = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 1.85rem;
  font-weight: 700;
  color: var(--text);
  line-height: 1.1;
`;

const StatSuffix = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const NewToday = styled.span`
  display: inline-block;
  margin-left: 0.35rem;
  padding: 0.15rem 0.45rem;
  font-size: 0.65rem;
  font-weight: 700;
  border-radius: 6px;
  background: color-mix(in srgb, var(--error, #c62828) 14%, transparent);
  color: var(--error, #c62828);
`;

const ProgressWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-top: 0.35rem;
`;

const ProgressTrack = styled.div`
  flex: 1;
  min-width: 80px;
  height: 6px;
  border-radius: 999px;
  background: var(--surfaceVariant);
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
  border-radius: 999px;
  background: var(--primary);
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 340px);
  gap: 1.5rem;
  align-items: start;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: 1fr;
  }
`;

const MainCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

const SideCol = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

const Panel = styled.section`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 16px;
  padding: 1.25rem 1.35rem 1.35rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
  border-radius: 12px;
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 200px;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.75rem;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const SearchIcon = styled.span`
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.9rem;
  flex-shrink: 0;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 0.2rem 0;
  font-size: 0.875rem;
  border: none;
  background: transparent;
  color: var(--text);
  &::placeholder {
    color: var(--textSecondary, var(--onSurfaceVariant));
  }
  &:focus {
    outline: none;
  }
`;

const Select = styled.select`
  padding: 0.5rem 1.75rem 0.5rem 0.65rem;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  max-width: 100%;
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary) 22%, transparent);
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin: 0 -0.15rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
  min-width: 640px;
  tbody tr {
    transition: background 0.12s ease;
  }
  tbody tr:hover {
    background: color-mix(in srgb, var(--surfaceVariant) 65%, transparent);
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 0.65rem 0.75rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--muted, var(--textSecondary, var(--onSurfaceVariant)));
  text-transform: uppercase;
  border-bottom: 1px solid var(--outline);
  background: var(--surfaceVariant);
`;

const Td = styled.td`
  padding: 0.85rem 0.75rem;
  vertical-align: middle;
  border-bottom: 1px solid var(--outline);
  color: var(--text);
`;

const StudentCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primaryDark) 100%);
  color: var(--onPrimary);
  font-size: 0.72rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
  display: block;
`;

const StudentName = styled.div`
  font-weight: 700;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CourseName = styled.div`
  font-size: 0.75rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AssignTitle = styled.div`
  font-weight: 700;
  color: var(--text);
`;

const AssignLink = styled(Link)`
  color: var(--primary);
  text-decoration: none;
  font-weight: 700;
  &:hover {
    color: var(--primaryDark);
    text-decoration: underline;
  }
`;

const AssignCat = styled.div`
  font-size: 0.78rem;
  font-style: italic;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const DateText = styled.span`
  color: var(--text);
  font-weight: 500;
`;

const Badge = styled.span<{ $variant: 'ready' | 'resubmitted' | 'late' | 'graded' }>`
  display: inline-block;
  padding: 0.28rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 8px;
  ${({ $variant }) => {
    if ($variant === 'ready')
      return `background: color-mix(in srgb, var(--primary) 14%, transparent); color: var(--primary);`;
    if ($variant === 'resubmitted')
      return `background: color-mix(in srgb, var(--warning, #b45309) 16%, transparent); color: var(--warning, #b45309);`;
    if ($variant === 'late')
      return `background: color-mix(in srgb, var(--error, #c62828) 14%, transparent); color: var(--error, #c62828);`;
    return `background: var(--surfaceVariant); color: var(--textSecondary, var(--onSurfaceVariant));`;
  }}
`;

const ActionBtns = styled.div`
  display: flex;
  gap: 0.35rem;
  align-items: center;
`;

const StartGradingBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  padding: 0 0.7rem;
  border-radius: 8px;
  border: 1px solid var(--primary);
  background: color-mix(in srgb, var(--primary) 10%, transparent);
  color: var(--primary);
  text-decoration: none;
  font-size: 0.78rem;
  font-weight: 700;
  font-family: 'Open Sans', sans-serif;
  white-space: nowrap;
  &:hover {
    border-color: var(--primaryDark);
    background: color-mix(in srgb, var(--primary) 16%, transparent);
  }
`;

const IconBtn = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid ${({ $primary }) => ($primary ? 'var(--primary)' : 'var(--outline)')};
  background: ${({ $primary }) =>
    $primary ? 'color-mix(in srgb, var(--primary) 10%, transparent)' : 'var(--surface)'};
  color: ${({ $primary }) => ($primary ? 'var(--primary)' : 'var(--textSecondary, var(--onSurfaceVariant))')};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  &:hover {
    background: var(--surfaceVariant);
    border-color: ${({ $primary }) => ($primary ? 'var(--primaryDark)' : 'var(--outline)')};
  }
`;

const ViewMore = styled.div`
  text-align: center;
  padding: 0.85rem 0 0;
`;

const ViewMoreBtn = styled.button`
  background: none;
  border: none;
  color: var(--primary);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Open Sans', sans-serif;
  text-decoration: underline;
  &:hover {
    color: var(--primaryDark);
  }
`;

const SideCard = styled(Panel)``;

const SideHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
`;

const SideTitle = styled.h3`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text);
`;

const SideIcon = styled.span<{ $tone?: 'primary' | 'danger' | 'warning' }>`
  display: inline-flex;
  color: ${({ $tone = 'primary' }) =>
    $tone === 'danger'
      ? 'var(--error, #c62828)'
      : $tone === 'warning'
        ? 'var(--warning, #e8a23c)'
        : 'var(--primary)'};
  font-size: 1rem;
`;

const RecentRow = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.55rem 0;
  border: none;
  border-bottom: 1px solid var(--outline);
  background: transparent;
  text-align: left;
  cursor: pointer;
  &:last-child {
    border-bottom: none;
  }
  &:hover .recent-chevron {
    color: var(--primary);
  }
`;

const RecentIcon = styled.span`
  font-size: 0.95rem;
  color: var(--primary);
  opacity: 0.9;
`;

const RecentBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const RecentName = styled.div`
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text);
`;

const RecentWork = styled.div`
  font-size: 0.75rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RecentScore = styled.span`
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--text);
  white-space: nowrap;
`;

const OutlineBtn = styled(Link)`
  display: block;
  margin-top: 0.85rem;
  padding: 0.55rem 0.85rem;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
  color: var(--text);
  border: 1px solid var(--outline);
  border-radius: 10px;
  text-decoration: none;
  background: var(--surfaceVariant);
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const DeadlineItem = styled.div`
  display: grid;
  grid-template-columns: 4px minmax(0, 1fr);
  gap: 0.65rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--outline);
  &:last-child {
    border-bottom: none;
  }
`;

const DeadlineLine = styled.div<{ $color: string }>`
  width: 4px;
  border-radius: 999px;
  background: ${({ $color }) => $color};
`;

const DeadlineUrgency = styled.div`
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: var(--textSecondary, var(--onSurfaceVariant));
  margin-bottom: 0.2rem;
`;

const DeadlineTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text);
`;

const DeadlineMeta = styled.div`
  font-size: 0.75rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  margin-top: 0.15rem;
`;

const PageFooter = styled.footer`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding-top: 1.5rem;
  margin-top: 0.5rem;
  border-top: 1px solid var(--outline);
  font-size: 0.68rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const FooterLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
`;

const FooterLink = styled.a`
  color: inherit;
  text-decoration: none;
  font-weight: 600;
  letter-spacing: 0.04em;
  &:hover {
    color: var(--primary);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 440px;
  background: var(--surface);
  border-radius: 16px;
  border: 1px solid var(--outline);
  padding: 1.25rem 1.35rem 1.35rem;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18);
`;

const ModalTitle = styled.h3`
  margin: 0 0 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  color: var(--text);
`;

const Label = styled.label`
  display: grid;
  gap: 0.35rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 0.65rem;
`;

const TextInput = styled.input`
  padding: 0.5rem 0.65rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--text);
  font: inherit;
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary) 20%, transparent);
  }
`;

const TextArea = styled.textarea`
  padding: 0.5rem 0.65rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--text);
  font: inherit;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary) 20%, transparent);
  }
`;

const ModalActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const Btn = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  padding: 0.55rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
  border-radius: 8px;
  cursor: pointer;
  border: ${({ $variant }) => ($variant === 'ghost' ? '1px solid var(--outline)' : 'none')};
  background: ${({ $variant }) => ($variant === 'ghost' ? 'transparent' : 'var(--primary)')};
  color: ${({ $variant }) => ($variant === 'ghost' ? 'var(--text)' : 'var(--onPrimary)')};
  transition: background 0.15s ease, border-color 0.15s ease;
  &:hover {
    background: ${({ $variant }) =>
      $variant === 'ghost' ? 'var(--surfaceVariant)' : 'var(--primaryDark)'};
    border-color: ${({ $variant }) => ($variant === 'ghost' ? 'var(--primary)' : 'transparent')};
  }
`;

const LoadingState = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--textSecondary);
`;

const DEADLINES = [
  {
    id: 'dl1',
    line: 'var(--error, #e53935)',
    label: 'IN 2 HOURS',
    title: 'Weekly Lab Summaries',
    course: 'Biology Section B',
    pending: 8,
  },
  {
    id: 'dl2',
    line: 'var(--warning, #fb8c00)',
    label: 'TOMORROW',
    title: 'AI Ethics Final Outline',
    course: 'Ethics & Society',
    pending: 12,
  },
  {
    id: 'dl3',
    line: 'var(--primary)',
    label: 'MONDAY',
    title: 'Thermodynamics Quiz',
    course: 'Physics I',
    pending: 22,
  },
] as const;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatSubmittedAt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function assignmentPathForRow(row: TeacherGradeRow): string | null {
  if (row.work_type !== 'assignment') return null;
  const workId = row.work_id?.trim();
  if (!workId) return null;
  return `/classroom-teacher/assignments/${encodeURIComponent(workId)}`;
}

function gradingPathForRow(row: TeacherGradeRow): string {
  return `/classroom-teacher/grades/${encodeURIComponent(row.id)}`;
}

function badgeForRow(row: TeacherGradeRow): { key: 'ready' | 'resubmitted' | 'late' | 'graded'; label: string } {
  if (row.status === 'graded' || row.status === 'returned') {
    return { key: 'graded', label: 'Graded' };
  }
  const q = row.queue_badge ?? (row.status === 'missing' ? 'late' : 'ready');
  if (q === 'ready') return { key: 'ready', label: 'Ready to Grade' };
  if (q === 'resubmitted') return { key: 'resubmitted', label: 'Resubmitted' };
  return { key: 'late', label: 'Late' };
}

const ClassroomTeacherGradesPage: React.FC = () => {
  const { classId } = useParams<{ classId?: string }>();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [score, setScore] = useState<number>(0);
  const [feedback, setFeedback] = useState('');
  const [search, setSearch] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'quiz' | 'assignment'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'graded'>('pending');
  const [showExtra, setShowExtra] = useState(false);

  const gradesQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'grades'],
    queryFn: teacherService.getGrades,
  });

  const saveGrade = useMutation({
    mutationFn: teacherService.saveGrade,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'grades'] });
      setSelectedId(null);
    },
  });

  const scopedClassId = classId?.trim() ?? '';
  const rows = useMemo(() => {
    const allRows = gradesQuery.data ?? [];
    if (!scopedClassId) return allRows;
    return allRows.filter((row) => (row.class_id ?? '').trim() === scopedClassId);
  }, [gradesQuery.data, scopedClassId]);

  const scopedClassName = useMemo(() => {
    if (!scopedClassId) return null;
    const name = rows.find((row) => row.class_name?.trim())?.class_name?.trim();
    return name ?? 'this class';
  }, [rows, scopedClassId]);

  const courses = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      if (r.class_name) set.add(r.class_name);
    });
    return Array.from(set).sort();
  }, [rows]);

  const queueRows = useMemo(
    () => rows.filter((r) => r.status === 'submitted' || r.status === 'missing'),
    [rows],
  );

  const recentlyGraded = useMemo(
    () =>
      rows
        .filter((r) => r.status === 'graded' || r.status === 'returned')
        .slice(0, 5),
    [rows],
  );

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const q = search.trim().toLowerCase();
      if (q) {
        const hay = `${row.student_name} ${row.work_title} ${row.class_name ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (courseFilter !== 'all' && row.class_name !== courseFilter) return false;
      if (typeFilter !== 'all' && row.work_type && row.work_type !== typeFilter) return false;

      if (statusFilter === 'pending') {
        return row.status === 'submitted' || row.status === 'missing';
      }
      if (statusFilter === 'graded') {
        return row.status === 'graded' || row.status === 'returned';
      }
      return true;
    });
  }, [rows, search, courseFilter, typeFilter, statusFilter]);

  const displayRows = showExtra ? filteredRows : filteredRows.slice(0, 8);

  const pendingCount = queueRows.length;
  const newToday = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return queueRows.filter((r) => r.submitted_at && new Date(r.submitted_at) >= start).length;
  }, [queueRows]);

  const submissionsToday = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return rows.filter((r) => r.submitted_at && new Date(r.submitted_at) >= start).length;
  }, [rows]);

  const submissionsPct = Math.min(100, Math.round((submissionsToday / 60) * 100));

  const selected = rows.find((row) => row.id === selectedId) ?? null;

  const openGrade = (row: TeacherGradeRow) => {
    setSelectedId(row.id);
    setScore(row.score ?? 0);
    setFeedback(row.feedback ?? '');
  };

  if (gradesQuery.isLoading) return <LoadingState>Loading grading queue…</LoadingState>;
  if (gradesQuery.isError) return <LoadingState>Unable to load submissions.</LoadingState>;

  return (
    <Page>
      <Hero>
        <HeroText>
          <HeroTitle>Grading &amp; Feedback</HeroTitle>
          <HeroSubtitle>
            {scopedClassId
              ? `Manage your evaluation queue and student progress for ${scopedClassName}.`
              : 'Manage your evaluation queue and student progress.'}
          </HeroSubtitle>
        </HeroText>
        <BulkButton to="/classroom-teacher/quizzes">
          <FaMagic aria-hidden />
          Bulk Grade Quiz Submissions
        </BulkButton>
      </Hero>

      <StatGrid>
        <StatCard $accent="primary">
          <StatLabel>PENDING REVIEWS</StatLabel>
          <StatValueRow>
            <StatValue>{pendingCount}</StatValue>
            {newToday > 0 ? <NewToday>+{newToday} new today</NewToday> : null}
          </StatValueRow>
        </StatCard>
        <StatCard $accent="warning">
          <StatLabel>AVG. FEEDBACK TIME</StatLabel>
          <StatValueRow>
            <StatValue>1.2</StatValue>
            <StatSuffix>hrs</StatSuffix>
          </StatValueRow>
        </StatCard>
        <StatCard $accent="deep">
          <StatLabel>SUBMISSIONS TODAY</StatLabel>
          <StatValueRow>
            <StatValue>{submissionsToday}</StatValue>
          </StatValueRow>
          <ProgressWrap>
            <ProgressTrack>
              <ProgressFill $pct={submissionsPct} />
            </ProgressTrack>
          </ProgressWrap>
        </StatCard>
      </StatGrid>

      <MainGrid>
        <MainCol>
          <Panel>
            <FilterRow>
              <SearchWrap>
                <SearchIcon>
                  <FaSearch />
                </SearchIcon>
                <SearchInput
                  placeholder="Search by student or assignment"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search submissions"
                />
              </SearchWrap>
              <Select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)} aria-label="Course">
                <option value="all">All Courses</option>
                {courses.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as 'all' | 'quiz' | 'assignment')}
                aria-label="Assignment type"
              >
                <option value="all">Assignment Type</option>
                <option value="assignment">Assignment</option>
                <option value="quiz">Quiz</option>
              </Select>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'pending' | 'graded')}
                aria-label="Status"
              >
                <option value="all">All status</option>
                <option value="pending">Needs grading</option>
                <option value="graded">Graded</option>
              </Select>
            </FilterRow>

            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>Student</Th>
                    <Th>Assignment</Th>
                    <Th>Date</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((row) => {
                    const b = badgeForRow(row);
                    const assignmentPath = assignmentPathForRow(row);
                    const gradingPath = gradingPathForRow(row);
                    return (
                      <tr key={row.id}>
                        <Td>
                          <StudentCell>
                            <Avatar aria-hidden>
                              {row.student_avatar_url ? (
                                <AvatarImage src={row.student_avatar_url} alt="" loading="lazy" />
                              ) : (
                                initials(row.student_name)
                              )}
                            </Avatar>
                            <div>
                              <StudentName>{row.student_name}</StudentName>
                              <CourseName>{row.class_name ?? '—'}</CourseName>
                            </div>
                          </StudentCell>
                        </Td>
                        <Td>
                          <AssignTitle>
                            {assignmentPath ? (
                              <AssignLink to={assignmentPath} aria-label={`Open assignment ${row.work_title}`}>
                                {row.work_title}
                              </AssignLink>
                            ) : (
                              row.work_title
                            )}
                          </AssignTitle>
                          {row.assignment_category ? <AssignCat>{row.assignment_category}</AssignCat> : null}
                        </Td>
                        <Td>
                          <DateText>{formatSubmittedAt(row.submitted_at)}</DateText>
                        </Td>
                        <Td>
                          <Badge $variant={b.key}>{b.label}</Badge>
                        </Td>
                        <Td>
                          <ActionBtns>
                            <StartGradingBtn to={gradingPath} aria-label={`Start grading ${row.student_name}`}>
                              Start Grading
                            </StartGradingBtn>
                            <IconBtn type="button" aria-label="Message student" title="Message">
                              <FaEnvelope />
                            </IconBtn>
                          </ActionBtns>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>

            {filteredRows.length > 8 ? (
              <ViewMore>
                <ViewMoreBtn type="button" onClick={() => setShowExtra((s) => !s)}>
                  {showExtra ? 'Show fewer submissions' : `View ${filteredRows.length - 8} more submissions`}
                </ViewMoreBtn>
              </ViewMore>
            ) : null}
          </Panel>
        </MainCol>

        <SideCol>
          <SideCard>
            <SideHead>
              <SideIcon>
                <FaCheck />
              </SideIcon>
              <SideTitle>Recently Graded</SideTitle>
            </SideHead>
            {recentlyGraded.length === 0 ? (
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--textSecondary)' }}>No graded items yet.</p>
            ) : (
              recentlyGraded.map((row) => (
                <RecentRow key={row.id} type="button" onClick={() => openGrade(row)}>
                  <RecentIcon>📄</RecentIcon>
                  <RecentBody>
                    <RecentName>{row.student_name}</RecentName>
                    <RecentWork>{row.work_title}</RecentWork>
                  </RecentBody>
                  <RecentScore>
                    {row.score ?? 0}/{row.max_points ?? 100}
                  </RecentScore>
                  <FaChevronRight className="recent-chevron" style={{ color: 'var(--outline)', fontSize: '0.75rem' }} />
                </RecentRow>
              ))
            )}
            <OutlineBtn to="/classroom-teacher/grades">View Full History</OutlineBtn>
          </SideCard>

          <SideCard>
            <SideHead>
              <SideIcon $tone="danger">
                <FaClock />
              </SideIcon>
              <SideTitle>Upcoming Deadlines</SideTitle>
            </SideHead>
            {DEADLINES.map((d) => (
              <DeadlineItem key={d.id}>
                <DeadlineLine $color={d.line} />
                <div>
                  <DeadlineUrgency>{d.label}</DeadlineUrgency>
                  <DeadlineTitle>{d.title}</DeadlineTitle>
                  <DeadlineMeta>
                    {d.course} · {d.pending} pending
                  </DeadlineMeta>
                </div>
              </DeadlineItem>
            ))}
          </SideCard>
        </SideCol>
      </MainGrid>

      <PageFooter>
        <span>© {new Date().getFullYear()} Digital Classroom. All rights reserved.</span>
        <FooterLinks>
          <FooterLink href="/privacy">Privacy Policy</FooterLink>
          <FooterLink href="/terms">Terms of Service</FooterLink>
          <FooterLink href="/help">Help Center</FooterLink>
        </FooterLinks>
      </PageFooter>

      {selected ? (
        <ModalOverlay
          role="dialog"
          aria-modal
          aria-labelledby="grade-modal-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSelectedId(null);
          }}
        >
          <ModalCard onMouseDown={(e) => e.stopPropagation()}>
            <ModalTitle id="grade-modal-title">Submission detail and feedback</ModalTitle>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.88rem' }}>
              <strong>{selected.student_name}</strong> — {selected.work_title}
            </p>
            <Label>
              Score (max {selected.max_points ?? 100})
              <TextInput
                type="number"
                min={0}
                value={Number.isNaN(score) ? 0 : score}
                onChange={(e) => setScore(Number(e.target.value))}
              />
            </Label>
            <Label>
              Feedback
              <TextArea rows={4} value={feedback} onChange={(e) => setFeedback(e.target.value)} />
            </Label>
            <ModalActions>
              <Btn
                type="button"
                onClick={() =>
                  saveGrade.mutate({
                    id: selected.id,
                    score,
                    feedback,
                    status: 'graded',
                  })
                }
                disabled={saveGrade.isPending}
              >
                Save score
              </Btn>
              <Btn
                type="button"
                $variant="ghost"
                onClick={() =>
                  saveGrade.mutate({
                    id: selected.id,
                    score,
                    feedback,
                    status: 'returned',
                  })
                }
                disabled={saveGrade.isPending}
              >
                Mark returned
              </Btn>
              <Btn type="button" $variant="ghost" onClick={() => setSelectedId(null)}>
                Cancel
              </Btn>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      ) : null}
    </Page>
  );
};

export default ClassroomTeacherGradesPage;
